export const isRangeValue = (value: unknown): value is RangeValue => {
  if (!value || typeof value !== "object") return false;
  return (
    "min_value" in value &&
    "max_value" in value &&
    typeof (value as any).min_value === "number" &&
    typeof (value as any).max_value === "number"
  );
};

export const isPartNumberValue = (value: unknown): value is PartNumber => {
  if (!value || typeof value !== "object") return false;
  return (
    "part_number" in value &&
    typeof (value as any).part_number === "string" &&
    "description" in value &&
    "name" in value &&
    "revision" in value
  );
};

const formatRangeValue = (value: RangeValue): string => {
  const minValue = Number.isFinite(value.min_value)
    ? String(value.min_value)
    : "";
  const maxValue = Number.isFinite(value.max_value)
    ? String(value.max_value)
    : "";
  if (!minValue && !maxValue) return "";
  if (!minValue) return `<= ${maxValue}`;
  if (!maxValue) return `>= ${minValue}`;
  return `${minValue}-${maxValue}`;
};

const formatPartNumberValue = (value: PartNumber): string => {
  if (!value.part_number) return "";
  const name = value.name ? ` - ${value.name}` : "";
  const revision = value.revision ? ` (rev ${value.revision})` : "";
  return `${value.part_number}${name}${revision}`;
};

export const formatValueType = (
  value: ValueType | null | undefined
): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "True" : "False";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) return value.join(", ");
  if (isRangeValue(value)) return formatRangeValue(value);
  if (isPartNumberValue(value)) return formatPartNumberValue(value);
  return "";
};

type PropertyDefinition = {
  data_type?: string | null;
};

// Enum helpers (shared by TrackingInputs and GridView)

export const isEnumDataType = (dataType: string | null | undefined): boolean =>
  ["ENUM", "MULTISELECT_ENUM"].includes(String(dataType ?? "").toUpperCase());

export const isMultiSelectEnumDataType = (
  dataType: string | null | undefined
): boolean => String(dataType ?? "").toUpperCase() === "MULTISELECT_ENUM";

/** Options from `config.enum_values`, trimmed and de-duplicated. */
export const getEnumValuesFromConfig = (
  config: Record<string, any> | null | undefined
): string[] => {
  if (!config || !Array.isArray(config.enum_values)) {
    return [];
  }

  return config.enum_values
    .map((value: unknown) => String(value).trim())
    .filter((value: string, index: number, values: string[]) => {
      return value !== "" && values.indexOf(value) === index;
    });
};

/** Match against options (exact, then case-insensitive); "" if blank, null if no match. */
export const normalizeEnumValue = (
  raw: string,
  enumValues: string[]
): string | null => {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return "";
  }

  const exactMatch = enumValues.find((option) => option === trimmed);
  if (exactMatch) {
    return exactMatch;
  }

  const lowered = trimmed.toLowerCase();
  const caseInsensitiveMatch = enumValues.find(
    (option) => option.toLowerCase() === lowered
  );
  return caseInsensitiveMatch || null;
};

/** Split a comma-separated string into trimmed, non-empty tokens. */
export const parseMultiEnumTokens = (raw: string): string[] => {
  return raw
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token !== "");
};

/** Serialize a selection to the FormSpec wire format, always in `enum_values` order. */
export const serializeMultiEnum = (
  selected: string[],
  enumValues: string[]
): string[] => enumValues.filter((option) => selected.includes(option));

/**
 * Parse a stored value into valid selected options. Accepts an array (the
 * FormSpec format) or a comma-separated string. Array elements that don't
 * match an option whole are split on commas, which recovers legacy values
 * saved as ["A, B"].
 */
export const parseMultiEnumValue = (
  value: unknown,
  enumValues: string[]
): string[] => {
  const tokens = Array.isArray(value)
    ? value.flatMap((element) => {
        const text = String(element);
        return normalizeEnumValue(text, enumValues)
          ? [text]
          : parseMultiEnumTokens(text);
      })
    : typeof value === "string"
    ? parseMultiEnumTokens(value)
    : [];
  const matched = tokens
    .map((token) => normalizeEnumValue(token, enumValues))
    .filter((token): token is string => !!token);
  return Array.from(new Set(matched));
};

export const seedValueForProperty = (
  definition: PropertyDefinition | null | undefined
): ValueType => {
  const dataType = definition?.data_type
    ? String(definition.data_type).toUpperCase()
    : "";
  if (dataType === "RANGE") {
    return { min_value: Number.NaN, max_value: Number.NaN } as RangeValue;
  }
  if (dataType === "BOOLEAN") {
    return false;
  }
  if (isMultiSelectEnumDataType(dataType)) {
    return [];
  }
  if (dataType === "PART_NUMBER") {
    return {
      part_number: "",
      name: null,
      description: null,
      revision: null,
    } as unknown as PartNumber;
  }
  return "";
};
